import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import { ArrowLeft, Phone } from 'lucide-react';

const PhoneVerificationPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUser } = useAuth();
  
  // Get phone and email from location state or query params
  const phone = (location.state as any)?.phone || new URLSearchParams(location.search).get('phone') || '';
  const email = (location.state as any)?.email || new URLSearchParams(location.search).get('email') || '';
  
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [error, setError] = useState('');
  const [otpEnabled, setOtpEnabled] = useState<boolean | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (!phone) {
      navigate('/login');
      return;
    }
    
    // Check if OTP feature is enabled
    const checkOTPEnabled = async () => {
      try {
        const response = await authAPI.checkOTPEnabled();
        if (response.success) {
          const enabled = response.data.enabled;
          setOtpEnabled(enabled);
          if (!enabled) {
            // OTP is disabled - allow user to continue without verification
            console.log('OTP feature is disabled - allowing user to continue');
            await refreshUser();
            const locationState = (location.state as any);
            const returnTo = locationState?.returnTo || '/';
            navigate(returnTo);
            return;
          }
          // Auto-send OTP when page loads and feature is enabled
          sendOTP();
        }
      } catch (error) {
        console.error('Failed to check OTP feature status:', error);
        // Default to enabled if check fails to avoid blocking users
        setOtpEnabled(true);
        sendOTP();
      }
    };
    
    checkOTPEnabled();
    
    // Start countdown timer
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone, navigate]);

  const sendOTP = async () => {
    // Check if OTP feature is enabled before sending
    if (otpEnabled === false) {
      // OTP is disabled - allow user to continue without verification
      console.log('OTP feature is disabled - allowing user to continue');
      await refreshUser();
      const locationState = (location.state as any);
      const returnTo = locationState?.returnTo || '/';
      navigate(returnTo);
      return;
    }
    
    setResendLoading(true);
    setError('');
    try {
      const response = await authAPI.sendOTP({ phone, email });
      if (response.success) {
        // Start 60 second countdown
        setCountdown(60);
        // Security: Never display OTP in UI or alerts
        // OTP should only be sent via SMS
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to send OTP';
      setError(errorMessage);
      
      // If error is due to feature being disabled, allow user to continue
      if (error.response?.data?.code === 'OTP_FEATURE_DISABLED') {
        console.log('OTP feature is disabled - allowing user to continue');
        await refreshUser();
        const locationState = (location.state as any);
        const returnTo = locationState?.returnTo || '/';
        navigate(returnTo);
      } else {
        alert(errorMessage);
      }
    } finally {
      setResendLoading(false);
    }
  };

  const handleOtpChange = (value: string, index: number) => {
    // Only allow numbers
    if (value && !/^\d+$/.test(value)) {
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits are entered
    if (value && index === 5 && newOtp.every((digit) => digit !== '')) {
      // Use the latest OTP value instead of relying on async state
      const latestOtpString = newOtp.join('');
      handleVerify(latestOtpString);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async (otpOverride?: string) => {
    const otpString = otpOverride ?? otp.join('');
    if (otpString.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await authAPI.verifyOTP({ phone, otp: otpString, email });
      if (response.success) {
        // If we have a token, update auth context
        if (response.data.token && response.data.user) {
          const { token, user } = response.data;
          
          // Store token and user
          localStorage.setItem('token', token);
          localStorage.setItem('user', JSON.stringify(user));
          
          // Refresh user data
          await refreshUser();
        }
        alert('Phone number verified successfully!');
        
        // Check if we need to return to a specific page
        const returnTo = (location.state as any)?.returnTo;
        if (returnTo) {
          navigate(returnTo);
        } else {
          navigate('/');
        }
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Invalid OTP';
      setError(errorMessage);
      // Clear OTP on error
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (countdown > 0) {
      alert(`You can resend OTP in ${countdown} seconds`);
      return;
    }
    await sendOTP();
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <button
            onClick={() => navigate('/login')}
            className="absolute left-4 top-4 p-2 text-gray-300 hover:text-white"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="flex justify-center mb-4">
            <div className="bg-gray-100 rounded-full p-3">
              <Phone className="h-8 w-8 text-black" />
            </div>
          </div>
          <h2 className="text-3xl font-extrabold text-white">Verify Phone Number</h2>
          <p className="mt-2 text-sm text-gray-700">
            We've sent a 6-digit verification code to
            <br />
            <span className="font-semibold text-white">{phone}</span>
          </p>
        </div>

        <div className="mt-8">
          <div className="flex justify-between gap-2 mb-4">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(ref) => {
                  inputRefs.current[index] = ref;
                }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpChange(e.target.value, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                className={`w-14 h-16 text-center text-2xl font-semibold border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-black ${
                  error ? 'border-red-500' : 'border-gray-300'
                }`}
              />
            ))}
          </div>

          {error && (
            <p className="text-red-600 text-sm text-center mb-4">{error}</p>
          )}

          <button
            onClick={() => handleVerify()}
            disabled={loading || otp.some((digit) => !digit)}
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? 'Verifying...' : 'Verify'}
          </button>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-700">
              Didn't receive the code?{' '}
              <button
                onClick={handleResend}
                disabled={resendLoading || countdown > 0}
                className="text-black font-medium hover:text-black500 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                {resendLoading ? 'Sending...' : countdown > 0 ? `Resend in ${countdown}s` : 'Resend OTP'}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhoneVerificationPage;

